import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Space,
  Statistic,
  Table,
  Divider,
  Alert,
  Modal,
} from 'antd';
import { ReloadOutlined, FileDoneOutlined, FileTextOutlined, PrinterOutlined } from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import dayjs from 'dayjs';

const formatFechaConHora = (timestamp) => {
  if (!timestamp) return '-';
  if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
    return dayjs(timestamp.replace(' ', 'T')).format('DD/MM/YYYY HH:mm');
  }
  return dayjs(timestamp).format('DD/MM/YYYY HH:mm');
};

const RendicionCaja = () => {
  const [turnos, setTurnos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [rendiciones, setRendiciones] = useState([]);
  const [yaRendido, setYaRendido] = useState(false);
  const [form] = Form.useForm();
  const [arqueoVisible, setArqueoVisible] = useState(false);
  const [arqueoData, setArqueoData] = useState(null);
  const [arqueoRendicion, setArqueoRendicion] = useState(null);
  const [loadingArqueo, setLoadingArqueo] = useState(false);
  const [controlesMovimientosVisible, setControlesMovimientosVisible] = useState(false);

  const loadTurnos = async () => {
    try {
      const r = await apiClient.get('/gestion/rendicion-caja/recursos/turnos');
      if (r.ok) {
        const data = await r.json();
        setTurnos(data);
        if (data.length > 0 && !form.getFieldValue('turno_caja_id')) {
          form.setFieldsValue({ turno_caja_id: data[0].tca_id });
        }
      }
    } catch (e) {
      message.error('Error al cargar turnos');
    }
  };

  const loadResumen = async () => {
    const turnoId = form.getFieldValue('turno_caja_id');
    const fecha = form.getFieldValue('fecha_turno');
    if (!turnoId || !fecha) {
      message.warning('Seleccioná turno y fecha del turno');
      return;
    }
    const fechaStr = dayjs(fecha).format('YYYY-MM-DD');
    setLoadingResumen(true);
    setResumen(null);
    setYaRendido(false);
    try {
      const [resResumen, resExiste] = await Promise.all([
        apiClient.get(`/gestion/rendicion-caja/recursos/turnos/${turnoId}/resumen?fecha_turno=${fechaStr}`),
        apiClient.get(`/gestion/rendicion-caja/existe?turno_caja_id=${turnoId}&fecha_turno=${fechaStr}`),
      ]);
      if (resResumen.ok) {
        const data = await resResumen.json();
        setResumen(data);
        form.setFieldsValue({ monto_efectivo_declarado: data.saldo_efectivo_esperado });
      } else {
        message.error('Error al cargar resumen del turno');
      }
      if (resExiste.ok) {
        const { existe } = await resExiste.json();
        setYaRendido(!!existe);
      }
    } catch (e) {
      message.error('Error al cargar resumen');
    } finally {
      setLoadingResumen(false);
    }
  };

  const loadRendiciones = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/gestion/rendicion-caja?limit=30');
      if (r.ok) {
        const data = await r.json();
        setRendiciones(data);
      }
    } catch (e) {
      message.error('Error al cargar rendiciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTurnos();
    loadRendiciones();
  }, []);

  const handleVerArqueo = async (record) => {
    setLoadingArqueo(true);
    setArqueoRendicion(record);
    setArqueoData(null);
    setArqueoVisible(true);
    try {
      const fechaStr = dayjs(record.rec_fecha_turno).format('YYYY-MM-DD');
      const r = await apiClient.get(`/gestion/rendicion-caja/recursos/turnos/${record.rec_turno_caja_id}/resumen?fecha_turno=${fechaStr}`);
      if (r.ok) {
        const data = await r.json();
        setArqueoData(data);
      } else {
        message.error('Error al cargar movimientos del turno');
        setArqueoVisible(false);
      }
    } catch (e) {
      message.error('Error al cargar arqueo');
      setArqueoVisible(false);
    } finally {
      setLoadingArqueo(false);
    }
  };

  const handleImprimirArqueo = () => {
    if (!arqueoData || !arqueoRendicion) return;
    // Usar valores de la rendición (snapshot al cierre), no del resumen recalculado que incluye el retiro
    const totalIng = parseFloat(arqueoRendicion.rec_total_ingresos != null ? arqueoRendicion.rec_total_ingresos : 0);
    const totalEgr = parseFloat(arqueoRendicion.rec_total_egresos != null ? arqueoRendicion.rec_total_egresos : 0);
    const sist = parseFloat(arqueoRendicion.rec_saldo_efectivo_esperado != null ? arqueoRendicion.rec_saldo_efectivo_esperado : 0);
    const decl = parseFloat(arqueoRendicion.rec_monto_efectivo_declarado != null ? arqueoRendicion.rec_monto_efectivo_declarado : 0);
    const diff = decl - sist;
    const diffTexto = diff === 0 ? '$0.00' : diff > 0 ? `+$${diff.toFixed(2)} (sobrante)` : `$${diff.toFixed(2)} (faltante)`;
    // Movimientos sin el retiro (Cierre de caja) para mostrar el estado al momento de la rendición
    const movsSinRetiro = (arqueoData.movimientos || []).filter((m) => !(m.caj_concepto || '').includes('Cierre de caja'));
    const formasPagoIng = (arqueoData.formas_pago || []).filter((fp) => fp.total_ingresos > 0).map((fp) => `${fp.forma_pago}: $${parseFloat(fp.total_ingresos).toFixed(2)}`).join(', ');
    const formasPagoEgr = totalEgr > 0 ? (arqueoData.formas_pago || []).filter((fp) => fp.total_egresos > 0).map((fp) => `${fp.forma_pago}: $${parseFloat(fp.total_egresos).toFixed(2)}`).join(', ') : '';
    const movRows = movsSinRetiro.map((m) => {
      const fechaHora = m.caj_fecha_creacion ? formatFechaConHora(m.caj_fecha_creacion) : (m.caj_fecha || '-');
      const cuenta = m.cuenta_nombre || m.caj_concepto || '-';
      const tipo = (m.caj_tipo_movimiento || '').toUpperCase();
      const monto = parseFloat(m.caj_monto || 0);
      const signo = m.caj_tipo_movimiento === 'ingreso' ? '+' : '-';
      return `<tr><td>${fechaHora}</td><td>${tipo}</td><td>${cuenta}</td><td>${m.caj_metodo_pago || '-'}</td><td style="text-align:right">${signo}$${Math.abs(monto).toFixed(2)}</td></tr>`;
    }).join('');
    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Arqueo de caja - ${arqueoRendicion.turno_nombre} - ${dayjs(arqueoRendicion.rec_fecha_turno).format('DD/MM/YYYY')}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .subt { font-size: 11px; color: #666; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
    th { background: #f5f5f5; font-weight: 600; }
    .resumen { margin: 16px 0; }
    .resumen p { margin: 4px 0; }
    .totales { font-weight: 600; margin-top: 12px; }
  </style>
</head>
<body>
  <h1>Arqueo de caja - Rendición</h1>
  <p class="subt">Turno: ${arqueoRendicion.turno_nombre} &nbsp;|&nbsp; Fecha turno: ${dayjs(arqueoRendicion.rec_fecha_turno).format('DD/MM/YYYY')} &nbsp;|&nbsp; Cierre: ${formatFechaConHora(arqueoRendicion.rec_fecha_hora_cierre)}</p>
  <p class="subt">Rango horario: ${arqueoData.desde || '-'} a ${arqueoData.hasta || '-'}</p>
  <div class="resumen">
    <p><strong>Total ingresos:</strong> $${totalIng.toFixed(2)} ${formasPagoIng ? `(${formasPagoIng})` : ''}</p>
    <p><strong>Total egresos:</strong> $${totalEgr.toFixed(2)} ${formasPagoEgr ? `(${formasPagoEgr})` : ''}</p>
    <p><strong>Efectivo (sistema):</strong> $${sist.toFixed(2)}</p>
    <p><strong>Efectivo declarado:</strong> $${decl.toFixed(2)}</p>
    <p class="totales"><strong>Diferencia (faltante/sobrante):</strong> ${diffTexto}</p>
  </div>
  <h2 style="font-size: 14px; margin-top: 20px;">Detalle de movimientos</h2>
  <table>
    <thead><tr><th>Fecha / Hora</th><th>Tipo</th><th>Concepto / Cuenta</th><th>Forma de pago</th><th>Monto</th></tr></thead>
    <tbody>${movRows || '<tr><td colspan="5">Sin movimientos</td></tr>'}</tbody>
  </table>
  <p style="margin-top: 16px; font-size: 11px; color: #888;">Documento generado para presentación de rendición de caja.</p>
</body>
</html>`;
    const ventana = window.open('', '_blank');
    ventana.document.write(html);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => { ventana.print(); }, 300);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        turno_caja_id: values.turno_caja_id,
        fecha_turno: dayjs(values.fecha_turno).format('YYYY-MM-DD'),
        monto_efectivo_declarado: values.monto_efectivo_declarado,
        observaciones: values.observaciones || null,
      };
      const r = await apiClient.post('/gestion/rendicion-caja', payload);
      if (r.ok) {
        message.success('Rendición registrada correctamente');
        setResumen(null);
        setYaRendido(true);
        loadRendiciones();
        form.setFieldsValue({ monto_efectivo_declarado: undefined, observaciones: undefined });
      } else {
        const err = await r.json();
        message.error(err.message || 'Error al registrar rendición');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('Error al enviar');
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <h1 style={{ marginBottom: 8, color: '#fff' }}>Rendición de caja</h1>
        <p style={{ color: '#b0b0b0', marginBottom: 24 }}>
          Al finalizar cada turno, cargá el efectivo que queda en caja. La fecha del turno es el día en que <strong>empieza</strong> el turno (ej. para el turno Noche 23/02 17:00 – 24/02 02:00, la fecha es 23/02).
        </p>

        <Card title="Cerrar turno" style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" initialValues={{ fecha_turno: dayjs() }} onValuesChange={() => setResumen(null)}>
            <Space wrap size="middle">
              <Form.Item name="turno_caja_id" label="Turno" rules={[{ required: true }]} style={{ marginBottom: 0, minWidth: 200 }}>
                <Select
                  placeholder="Seleccionar turno"
                  options={turnos.map((t) => ({ label: `${t.tca_nombre} (${String(t.tca_hora_inicio).substring(0, 5)} - ${String(t.tca_hora_fin).substring(0, 5)})`, value: t.tca_id }))}
                />
              </Form.Item>
              <Form.Item name="fecha_turno" label="Fecha del turno" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <DatePicker format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item label=" " style={{ marginBottom: 0 }}>
                <Button type="primary" onClick={loadResumen} loading={loadingResumen}>
                  Ver resumen del turno
                </Button>
              </Form.Item>
            </Space>
          </Form>

          {yaRendido && resumen && (
            <Alert type="info" message="Ya existe una rendición para este turno en esta fecha." style={{ marginTop: 16 }} />
          )}

          {resumen && !yaRendido && (
            <>
              <Divider />
              <div style={{ marginBottom: 16 }}>
                <strong style={{ color: '#b0b0b0' }}>Rango:</strong> {resumen.desde} → {resumen.hasta}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 24, alignItems: 'flex-start' }}>
                <div>
                  <Statistic title="Total ingresos" value={resumen.total_ingresos} prefix="$" valueStyle={{ color: '#52c41a' }} />
                  {resumen.formas_pago && resumen.formas_pago.length > 0 && (
                    <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid #52c41a' }}>
                      {resumen.formas_pago
                        .filter((fp) => fp.total_ingresos > 0)
                        .map((fp) => (
                          <div key={fp.forma_pago} style={{ color: '#b0b0b0', fontSize: 13 }}>
                            {fp.forma_pago}: <span style={{ color: '#52c41a' }}>${fp.total_ingresos.toFixed(2)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <div>
                  <Statistic title="Total egresos" value={resumen.total_egresos} prefix="$" valueStyle={{ color: '#ff4d4f' }} />
                  {resumen.formas_pago && resumen.formas_pago.length > 0 && (
                    <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid #ff4d4f' }}>
                      {resumen.formas_pago
                        .filter((fp) => fp.total_egresos > 0)
                        .map((fp) => (
                          <div key={fp.forma_pago} style={{ color: '#b0b0b0', fontSize: 13 }}>
                            {fp.forma_pago}: <span style={{ color: '#ff4d4f' }}>${fp.total_egresos.toFixed(2)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <div>
                  <Statistic
                    title={<span>Efectivo esperado <span style={{ fontWeight: 'normal', fontSize: 12, color: '#888' }}>(solo efectivo: ingresos − egresos)</span></span>}
                    value={resumen.saldo_efectivo_esperado}
                    prefix="$"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </div>
                <div>
                  <Statistic title="Movimientos" value={resumen.cantidad_movimientos} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => setControlesMovimientosVisible(true)}
                >
                  Controles de movimientos
                </Button>
              </div>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                  name="monto_efectivo_declarado"
                  label="Efectivo que se entrega ($)"
                  rules={[{ required: true }]}
                >
                  <Input type="number" step={0.01} min={0} placeholder="Monto contado en caja" />
                </Form.Item>
                <Form.Item name="observaciones" label="Observaciones">
                  <Input.TextArea rows={2} placeholder="Opcional" />
                </Form.Item>
                <Button type="primary" htmlType="submit" icon={<FileDoneOutlined />}>
                  Registrar rendición
                </Button>
              </Form>
            </>
          )}
        </Card>

        <Modal
          title={() => {
            const turnoId = form.getFieldValue('turno_caja_id');
            const turno = turnos.find((t) => t.tca_id === turnoId);
            const fecha = form.getFieldValue('fecha_turno');
            const fechaStr = fecha ? dayjs(fecha).format('DD/MM/YYYY') : '';
            return `Control de movimientos - ${turno?.tca_nombre || 'Turno'}${fechaStr ? ` - ${fechaStr}` : ''}`;
          }}
          open={controlesMovimientosVisible}
          onCancel={() => setControlesMovimientosVisible(false)}
          width={950}
          footer={[
            <Button key="cerrar" onClick={() => setControlesMovimientosVisible(false)}>Cerrar</Button>,
          ]}
        >
          {!resumen && <Alert type="info" message="Primero hacé clic en “Ver resumen del turno”." />}
          {resumen && (
            <>
              <div style={{ marginBottom: 12 }}>
                <p style={{ color: '#b0b0b0', marginBottom: 8 }}>
                  <strong>Rango:</strong> {resumen.desde} → {resumen.hasta}
                </p>
                <Space wrap size="large">
                  <Statistic title="Ingresos" value={resumen.total_ingresos} prefix="$" valueStyle={{ color: '#52c41a', fontSize: 16 }} />
                  <Statistic title="Egresos" value={resumen.total_egresos} prefix="$" valueStyle={{ color: '#ff4d4f', fontSize: 16 }} />
                  <Statistic title="Efectivo esperado" value={resumen.saldo_efectivo_esperado} prefix="$" valueStyle={{ color: '#1890ff', fontSize: 16 }} />
                  <Statistic title="Movimientos" value={resumen.cantidad_movimientos} valueStyle={{ fontSize: 16 }} />
                </Space>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <Table
                size="small"
                dataSource={resumen.movimientos || []}
                rowKey={(r, i) => r.caj_id || i}
                pagination={{ pageSize: 12 }}
                columns={[
                  { title: 'Fecha / Hora', key: 'fecha', width: 150, render: (_, m) => formatFechaConHora(m.caj_fecha_creacion || m.caj_fecha) },
                  { title: 'Tipo', dataIndex: 'caj_tipo_movimiento', key: 'tipo', width: 90, render: (t) => (t || '').toUpperCase() },
                  { title: 'Cuenta / Concepto', key: 'cuenta', render: (_, m) => m.cuenta_nombre || m.caj_concepto || '-' },
                  { title: 'Comprobante', dataIndex: 'numero_comprobante', key: 'comp', width: 140, render: (v) => v || '-' },
                  { title: 'Forma de pago', dataIndex: 'caj_metodo_pago', key: 'fp', width: 120, render: (v) => v || '-' },
                  {
                    title: 'Monto',
                    dataIndex: 'caj_monto',
                    key: 'monto',
                    width: 120,
                    align: 'right',
                    render: (v, m) => (
                      <span style={{ color: m.caj_tipo_movimiento === 'ingreso' ? '#52c41a' : '#ff4d4f' }}>
                        {m.caj_tipo_movimiento === 'ingreso' ? '+' : '-'}${parseFloat(v || 0).toFixed(2)}
                      </span>
                    ),
                  },
                ]}
              />
            </>
          )}
        </Modal>

        <Card title="Últimas rendiciones" extra={<Button icon={<ReloadOutlined />} onClick={loadRendiciones} loading={loading} size="small">Actualizar</Button>}>
          <Table
            dataSource={rendiciones}
            rowKey="rec_id"
            loading={loading}
            size="small"
            columns={[
              { title: 'Fecha turno', dataIndex: 'rec_fecha_turno', key: 'fecha', render: (d) => dayjs(d).format('DD/MM/YYYY') },
              { title: 'Turno', dataIndex: 'turno_nombre', key: 'turno' },
              { title: 'Cierre', dataIndex: 'rec_fecha_hora_cierre', key: 'cierre', render: formatFechaConHora },
              { title: 'Ingresos', dataIndex: 'rec_total_ingresos', key: 'ing', render: (v) => `$${parseFloat(v || 0).toFixed(2)}` },
              { title: 'Egresos', dataIndex: 'rec_total_egresos', key: 'egr', render: (v) => `$${parseFloat(v || 0).toFixed(2)}` },
              {
                title: 'Efectivo (sistema)',
                dataIndex: 'rec_saldo_efectivo_esperado',
                key: 'efectivo_sistema',
                render: (v) => `$${parseFloat(v != null ? v : 0).toFixed(2)}`,
              },
              {
                title: 'Efectivo declarado',
                dataIndex: 'rec_monto_efectivo_declarado',
                key: 'efectivo_declarado',
                render: (v) => `$${parseFloat(v || 0).toFixed(2)}`,
              },
              {
                title: 'Faltante / Sobrante',
                key: 'diferencia',
                width: 220,
                render: (_, record) => {
                  const sistema = parseFloat(record.rec_saldo_efectivo_esperado != null ? record.rec_saldo_efectivo_esperado : 0);
                  const declarado = parseFloat(record.rec_monto_efectivo_declarado != null ? record.rec_monto_efectivo_declarado : 0);
                  const diff = declarado - sistema;
                  let diffText = '$0.00';
                  if (diff > 0) diffText = `+$${diff.toFixed(2)} (sobrante)`;
                  else if (diff < 0) diffText = `$${diff.toFixed(2)} (faltante)`;
                  return (
                    <Space>
                      <span style={{ color: diff === 0 ? '#b0b0b0' : diff > 0 ? '#52c41a' : '#ff4d4f' }}>{diffText}</span>
                      <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => handleVerArqueo(record)}
                      >
                        Ver arqueo
                      </Button>
                    </Space>
                  );
                },
              },
            ]}
            pagination={{ pageSize: 10 }}
          />
        </Card>

        <Modal
          title={arqueoRendicion ? `Arqueo de caja - ${arqueoRendicion.turno_nombre} - ${dayjs(arqueoRendicion.rec_fecha_turno).format('DD/MM/YYYY')}` : 'Arqueo de caja'}
          open={arqueoVisible}
          onCancel={() => { setArqueoVisible(false); setArqueoData(null); setArqueoRendicion(null); }}
          width={800}
          footer={[
            <Button key="cerrar" onClick={() => { setArqueoVisible(false); setArqueoData(null); setArqueoRendicion(null); }}>Cerrar</Button>,
            arqueoData && (
              <Button key="imprimir" type="primary" icon={<PrinterOutlined />} onClick={handleImprimirArqueo}>
                Imprimir / Guardar PDF
              </Button>
            ),
          ]}
        >
          {loadingArqueo && <div style={{ padding: 24, textAlign: 'center' }}>Cargando movimientos...</div>}
          {!loadingArqueo && arqueoData && arqueoRendicion && (
            <>
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: '#b0b0b0', marginBottom: 8 }}><strong>Rango:</strong> {arqueoData.desde} → {arqueoData.hasta}</p>
                <Space wrap size="large">
                  <Statistic title="Ingresos" value={arqueoRendicion.rec_total_ingresos} prefix="$" valueStyle={{ color: '#52c41a', fontSize: 16 }} />
                  <Statistic title="Egresos" value={arqueoRendicion.rec_total_egresos} prefix="$" valueStyle={{ color: '#ff4d4f', fontSize: 16 }} />
                  <Statistic title="Efectivo (sistema)" value={arqueoRendicion.rec_saldo_efectivo_esperado} prefix="$" valueStyle={{ fontSize: 16 }} />
                  <Statistic title="Efectivo declarado" value={arqueoRendicion.rec_monto_efectivo_declarado} prefix="$" valueStyle={{ fontSize: 16 }} />
                  <div>
                    <div style={{ color: '#b0b0b0', fontSize: 12, marginBottom: 2 }}>Faltante / Sobrante</div>
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: (() => {
                          const sist = parseFloat(arqueoRendicion.rec_saldo_efectivo_esperado != null ? arqueoRendicion.rec_saldo_efectivo_esperado : 0);
                          const decl = parseFloat(arqueoRendicion.rec_monto_efectivo_declarado || 0);
                          const d = decl - sist;
                          return d === 0 ? '#b0b0b0' : d > 0 ? '#52c41a' : '#ff4d4f';
                        })(),
                      }}
                    >
                      {(() => {
                        const sist = parseFloat(arqueoRendicion.rec_saldo_efectivo_esperado != null ? arqueoRendicion.rec_saldo_efectivo_esperado : 0);
                        const decl = parseFloat(arqueoRendicion.rec_monto_efectivo_declarado || 0);
                        const d = decl - sist;
                        return `${d >= 0 ? '+' : ''}$${d.toFixed(2)}`;
                      })()}
                    </span>
                  </div>
                </Space>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <h4 style={{ marginBottom: 8, color: '#fff' }}>Movimientos del turno (al momento del cierre)</h4>
              <Table
                size="small"
                dataSource={(arqueoData.movimientos || []).filter((m) => !(m.caj_concepto || '').includes('Cierre de caja'))}
                rowKey={(r, i) => r.caj_id || i}
                pagination={{ pageSize: 8 }}
                columns={[
                  { title: 'Fecha / Hora', key: 'fecha', width: 140, render: (_, m) => formatFechaConHora(m.caj_fecha_creacion || m.caj_fecha) },
                  { title: 'Tipo', dataIndex: 'caj_tipo_movimiento', key: 'tipo', width: 90, render: (t) => (t || '').toUpperCase() },
                  { title: 'Concepto / Cuenta', key: 'cuenta', render: (_, m) => m.cuenta_nombre || m.caj_concepto || '-' },
                  { title: 'Forma de pago', dataIndex: 'caj_metodo_pago', key: 'fp', width: 100 },
                  {
                    title: 'Monto',
                    dataIndex: 'caj_monto',
                    key: 'monto',
                    width: 100,
                    render: (v, m) => (
                      <span style={{ color: m.caj_tipo_movimiento === 'ingreso' ? '#52c41a' : '#ff4d4f' }}>
                        {m.caj_tipo_movimiento === 'ingreso' ? '+' : '-'}${parseFloat(v || 0).toFixed(2)}
                      </span>
                    ),
                  },
                ]}
              />
            </>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default RendicionCaja;
